import { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { Flex, Typography, App, DatePicker, Button, Pagination } from "antd";

import dayjs from "dayjs";

import PlusIcon from "@/icons/PlusIcon";

import TimeTrackingTable from "@/components/tables/TimeTrackingTable";
import TimeTrackingModal from "@/components/modals/TimeTrackingModal";
import Breadcrumbs from "@/components/ui/Breadcrumbs";
import CustomSelect from "@/components/inputs/CustomSelect";
import SearchFrom3 from "@/components/inputs/SearchFrom3";

import { UserContext } from "@/context/UserContext";

import { useTableAutoHeight } from "@/hooks/useTableAutoHeight";

import { getList } from "@/api/workrecords";
import { getUnitsOptions } from "@/api/units";

import { cleanParams, getDaysInMonth, getFirstDay } from "@/utils";
import { SORT_KEYS } from "@/constants/employees";

const LOCAL_STORAGE_KEY = "timeTrackingTableParams";

const { Title } = Typography;
const dateFormat = "MMMM YYYY";

// Страница табельного учета времени сотрудников
const TimeTracking = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(UserContext);

  // Данные
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Инициализация пагинации из localStorage
  const initialTableParams = JSON.parse(
    localStorage.getItem(`${user.user_uuid}_${LOCAL_STORAGE_KEY}`)
  ) || {
    pagination: {
      current: 1,
      pageSize: 10,
      placement: ["bottomLeft"],
      showSizeChanger: true,
    },
  };
  const [tableParams, setTableParams] = useState(initialTableParams);

  // Автоматическая высота таблицы
  const { tableContainerRef, tableHeight, headerHeight } = useTableAutoHeight([
    data,
    tableParams.pagination.pageSize,
  ]);

  // Модальное окно добавления новой записи
  const [isModalShow, setIsModalShow] = useState(false);
  const [newRecord, setNewRecord] = useState(null);

  // Отделы для фильтра
  const [units, setUnits] = useState([]);

  // Фильтры:
  // Обязательный - установка месяца и года
  // Отдел, поиск
  const [filters, setFilters] = useState(() => {
    const params = new URLSearchParams(location.search);
    const initial = {
      search: "",
      unit_uuid: null,
      month: dayjs().month() + 1,
      year: dayjs().year(),
    };
    for (const key in initial) {
      if (params.has(key)) {
        const value = params.get(key);

        // Проверяем, является ли значение флага строкой "true" или "false"
        if (value === "true") {
          initial[key] = true;
        } else if (value === "false") {
          initial[key] = false;
        } else {
          initial[key] = value;
        }

        if (key === "month" || key === "year") initial[key] = Number(value);
      }
    }

    return initial;
  });

  // Запрос на сервер для получения данных
  const fetchData = async (pagination, sorter) => {
    setIsLoading(true);

    const params = {
      page: pagination?.current || tableParams.pagination.current,
      perpage: pagination?.pageSize || tableParams.pagination.pageSize,
      is_dismissed: false,
      ...filters,
    };

    if (sorter) {
      const sortArray = Array.isArray(sorter) ? sorter : [sorter];

      const sortString = sortArray
        .map(({ field, order }) => {
          const name = SORT_KEYS[field];
          return order === "descend" ? `-${name}` : name;
        })
        .join(",");

      params.sort = sortString;
    }

    try {
      const { employees, total, page, perPage } = await getList(params);
      setData(employees);
      const updatedTableParams = {
        pagination: {
          ...tableParams.pagination,
          total,
          current: page,
          pageSize: perPage,
        },
      };
      setTableParams(updatedTableParams);

      // Сохраняем параметры пагинации в localStorage
      localStorage.setItem(
        `${user.user_uuid}_${LOCAL_STORAGE_KEY}`,
        JSON.stringify(updatedTableParams)
      );
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOptions = async () => {
    try {
      const { units } = await getUnitsOptions();

      setUnits(units);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    }
  };

  // initial fetch
  useEffect(() => {
    fetchOptions();
    // eslint-disable-next-line
  }, []);

  // Сменили фильтры, сменили URL
  useEffect(() => {
    const cleanedFilters = cleanParams(filters);
    const params = new URLSearchParams(cleanedFilters);
    navigate({ search: params.toString() }, { replace: true });
    fetchData({ current: 1 });
    // eslint-disable-next-line
  }, [filters]);

  // Обработчик для смены фильтров
  const handleFilterChange = (name, value) => {
    if (Array.isArray(name)) {
      const res = name.reduce((acc, val, idx) => {
        acc[val] = value[idx];
        return acc;
      }, {});

      setFilters({
        ...filters,
        ...res,
      });
    } else {
      setFilters({
        ...filters,
        [name]: value || "",
      });
    }
  };

  // Обработчик смены даты в календаре
  const handleDateChange = (date) => {
    handleFilterChange(["month", "year"], [date.month() + 1, date.year()]);
  };

  const handleTableChange = async (pagination, filters, sorter) => {
    fetchData(pagination, sorter);
  };

  const handlePaginationChange = (current, pageSize) => {
    // При изменении pageSize сбрасываем страницу на 1
    const shouldResetPage = pageSize !== tableParams.pagination.pageSize;

    const newPagination = {
      ...tableParams.pagination,
      current: shouldResetPage ? 1 : current,
      pageSize,
    };

    fetchData(newPagination, tableParams.sorter);
  };

  // Хотим добавить новую запись (через таблицу)
  const handleAddRecord = (record) => {
    const { month, year } = filters;
    const { day, unit_uuid, uuid, hours, record_uuid, description } = record;

    setNewRecord({
      employee_uuid: uuid,
      unit_uuid,
      worked_hours: hours,
      uuid: record_uuid,
      description,
      // 'YYYY-MM-DD' format
      work_date: dayjs(`${year}-${month}-${day}`),
    });
    setIsModalShow(true);
  };

  // Через общую кнопку
  const handleAddNew = () => {
    setNewRecord({});
    setIsModalShow(true);
  };

  // После закрытия модалки, обновляем данные
  const handleSaveRecord = () => {
    setIsModalShow(false);
    fetchData();
  };

  return (
    <>
      <Flex vertical gap={25} className="h-100">
        <Flex vertical gap={10}>
          <Breadcrumbs
            items={[
              {
                title: "Административная панель",
              },
              {
                title: "Табель",
              },
            ]}
          />
          <Flex gap={20} align="center" justify="space-between">
            <Title level={2} className="no-margin">
              Табель учета рабочего времени
            </Title>

            <Button
              type="primary"
              icon={<PlusIcon />}
              onClick={handleAddNew}
            ></Button>
          </Flex>
        </Flex>

        <Flex gap={20} align="center">
          <DatePicker
            allowClear={false}
            picker="month"
            format={dateFormat}
            defaultValue={dayjs(`${filters.year}-${filters.month + 1}-0`)}
            onChange={handleDateChange}
          />

          <CustomSelect
            placeholder="Отдел"
            options={units}
            fieldNames={{
              label: "name",
              value: "uuid",
              key: "uuid",
            }}
            style={{
              width: 200,
            }}
            showSearch
            optionFilterProp="name"
            allowClear
            defaultValue={filters.unit_uuid}
            onChange={(unit) => handleFilterChange("unit_uuid", unit)}
          />

          <SearchFrom3
            placeholder="Поиск"
            style={{ width: 300 }}
            defaultValue={filters.search}
            onSearch={(search) => handleFilterChange("search", search)}
          />
        </Flex>

        <div ref={tableContainerRef} style={{ flex: 1, minHeight: 0 }}>
          <TimeTrackingTable
            data={data}
            dayCount={getDaysInMonth(filters.month, filters.year)}
            firstDay={getFirstDay(filters.month, filters.year)}
            loading={isLoading}
            pagination={false}
            onAddRecord={handleAddRecord}
            onChange={handleTableChange}
            scroll={{ x: "100%", y: tableHeight - headerHeight }}
          />
        </div>

        <div style={{ minHeight: 32 }}>
          <Pagination
            {...tableParams.pagination}
            onChange={handlePaginationChange}
          />
        </div>
      </Flex>
      {isModalShow && (
        <TimeTrackingModal
          data={newRecord}
          visible={isModalShow}
          onSave={handleSaveRecord}
          onCancel={() => setIsModalShow(false)}
        />
      )}
    </>
  );
};

export default TimeTracking;
